import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { GallusToolsProduct, PillInfo, ServiceStatus } from "./types/gallus";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { FetchBaseQueryArgs } from "@reduxjs/toolkit/dist/query/fetchBaseQuery";
import { getUserSessionItem } from "..";
import { CognitoOidcConfig } from "../CognitoOidcConfig";

const gallusToolsApiBaseURL: FetchBaseQueryArgs = {
  baseUrl: "https://gallus-tools.gallusanalytics.com/api/",
};

function extractGuidFromUrl(url: string): string | null {
  // Decode URL in case it's encoded (e.g., %2F -> /)
  const decodedUrl = decodeURIComponent(url);

  // Match any of the patterns that include "pinboard/{GUID}"
  const match = decodedUrl.match(/pinboard\/([a-zA-Z0-9-]+)/);

  // Return the GUID if found, otherwise null
  return match ? "pinboard/" + match[1] : null;
}

const baseQuery = fetchBaseQuery(gallusToolsApiBaseURL);
const baseQueryWithErrorHandling: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  return result;
};

export const gallusToolsApi = createApi({
  reducerPath: "gallusToolsApi",
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ["Pills", "Product"],
  endpoints: (builder) => ({
    GetAllPills: builder.query<PillInfo[], void>({
      query: () => {
        return {
          url: `Pills/GetAllPills`,
          method: "get",
        };
      },
    }),
    GetPillsByWorkspace: builder.query<PillInfo[], { workspace: string }>({
      query: (args) => {
        let urlSearchParams = new URLSearchParams();
        urlSearchParams.set("pathLevel", args.workspace);
        return {
          url: `Pills/GetAllPillsByPathLevel?${urlSearchParams.toString()}`,
          method: "get",
        };
      },
      providesTags: ["Pills"],
    }),
    AddPill: builder.mutation<
      boolean,
      { title: string; url: string; workspace: string }
    >({
      query: (args) => {
        const { title, url, workspace } = args;

        const formattedLink = extractGuidFromUrl(url);

        const bodyFormData = {
          title: title,
          link: formattedLink,
          pathLevel: workspace,
          styleTitle: "",
          description: "",
          styleDescription: "",
          styleLink: "",
          active: true,
          currentlyDisabled: false,
        };

        return {
          url: `Pills/`,
          body: bodyFormData,
          method: "post",
        };
      },
      invalidatesTags: ["Pills"],
    }),
    UpdatePill: builder.mutation<
      boolean,
      {
        id: string;
        title: string;
        url: string;
        workspace: string;
        currentlyDisabledState: boolean;
      }
    >({
      query: (args) => {
        const { id, title, url, workspace, currentlyDisabledState } = args;

        const formattedLink = extractGuidFromUrl(url);

        const bodyFormData = {
          id: id,
          title: title,
          link: formattedLink,
          description: "",
          pathLevel: workspace,
          styleDescription: "",
          styleLink: "",
          styleTitle: "",
          active: true,
          currentlyDisabled: currentlyDisabledState,
        };

        return {
          url: `Pills/`,
          body: bodyFormData,
          method: "put",
        };
      },
      invalidatesTags: ["Pills"],
    }),
    DeletePill: builder.mutation<PillInfo[], { pillID: number }>({
      query: (args) => {
        const bodyFormData = {
          id: args.pillID.toString(),
          title: "",
          link: "",
          pathLevel: "",
          styleTitle: "",
          description: "",
          styleDescription: "",
          styleLink: "",
        };
        return {
          url: "Pills/",
          body: bodyFormData,
          method: "Delete",
        };
      },
      invalidatesTags: ["Pills"],
    }),
    GetAllProducts: builder.mutation<GallusToolsProduct[], void>({
      query: () => {
        return {
          url: "Product/GetAllProducts",
        };
      },
      invalidatesTags: ["Product"],
    }),
    UpdateProductStatusByName: builder.mutation<
      any,
      {
        productNameArray: string[];
        newIsDisabledStatus: boolean;
      }
    >({
      query: (args) => {
        const idTokenVariable = getUserSessionItem("idToken");
        const accessTokenVariable = getUserSessionItem("accessToken");
        const clientIdVariable = CognitoOidcConfig.clientId;

        const bodyFormData = {
          productNames: args.productNameArray,
          setTo: args.newIsDisabledStatus,
          validationConfig: {
            idCliente: clientIdVariable,
            accessToken: accessTokenVariable,
            idToken: idTokenVariable,
          },
        };

        return {
          url: `/Product/UpdateAllProductByName`,
          body: bodyFormData,
          method: "put",
        };
      },
      invalidatesTags: ["Product"],
    }),
    IsProductDisabled: builder.query<boolean, { productName: string }>({
      query: (args) => {
        let urlSearchParams = new URLSearchParams();
        urlSearchParams.set("productName", args.productName);
        return {
          url: `Product/GetProductByName?${urlSearchParams.toString()}`,
        };
      },
      providesTags: ["Product"],
    }),
    GetStatus: builder.query<ServiceStatus[], void>({
      query: () => {
        return {
          url: `status`,
        };
      },
    }),
  }),
});

export const {
  useLazyGetAllPillsQuery,
  useLazyGetPillsByWorkspaceQuery,
  useAddPillMutation,
  useUpdatePillMutation,
  useDeletePillMutation,
  useUpdateProductStatusByNameMutation,
  useIsProductDisabledQuery,
  useLazyIsProductDisabledQuery,
  useGetStatusQuery,
} = gallusToolsApi;
